import React, { type ChangeEvent } from 'react';
import {
    FormControl,
    InputLabel,
    Select,
    type SelectChangeEvent,
    Radio,
    Switch,
    Button,
    Checkbox,
    FormControlLabel,
    TextField,
    MenuItem,
    ListItemText,
    type SxProps,
    type Theme,
} from '@mui/material';

import ErrorMessage from './ErrorMessage';
import { type Option, renderOptions } from '@/src/utils';
import type { Colors } from './styling';

interface SimpleSingleSelectProps {
    name: string;
    label: string;
    value: number;
    onChange: (event: SelectChangeEvent<number>) => void;
    options: Array<Option>;
    sx?: SxProps<Theme>;
    error?: any;
    disabled?: boolean;
}

export const SimpleSingleSelect: React.FC<SimpleSingleSelectProps> = ({
    name,
    label,
    value,
    onChange,
    options,
    sx = { width: '100%', mb: 2 },
    error,
    disabled,
}) => {
    const menuProps = {
        PaperProps: {
            style: {
                maxWidth: 400,
                overflowX: 'hidden' as const,
                textOverflow: 'ellipsis',
            },
        },
    };

    const renderValueStyle: React.CSSProperties = {
        display: 'block',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    };
    return (
        <FormControl size="small" disabled={disabled} sx={sx}>
            <InputLabel id={name}>{label}</InputLabel>
            <Select
                labelId={name}
                label={label}
                value={value}
                onChange={onChange}
                MenuProps={menuProps}
                sx={{ minWidth: 150, width: 150, ...sx }}
                renderValue={(selected) => {
                    const selectedOption = options.find((opt) => opt.id === selected);
                    return (
                        <span
                            style={renderValueStyle}
                            title={selectedOption ? selectedOption.name : ''}
                        >
                            {selectedOption ? selectedOption.name : ''}
                        </span>
                    );
                }}
            >
                {renderOptions(options)}
            </Select>
            <ErrorMessage error={error} />
        </FormControl>
    );
};

export type SimpleButtonVariant = 'text' | 'outlined' | 'contained';

interface SimpleButtonProps {
    onMouseDown?: (e: React.SyntheticEvent) => void;
    onClick?: (() => void) | ((e: React.SyntheticEvent) => void);
    className?: string;
    type?: 'button' | 'submit' | 'reset';
    variant?: 'text' | 'outlined' | 'contained';
    color?: Colors;
    children: React.ReactNode;
    sx?: SxProps<Theme>;
    disabled?: boolean;
    autoFocus?: boolean;
}

export const SimpleButton: React.FC<SimpleButtonProps> = ({
    onMouseDown,
    onClick,
    className = '',
    type = 'button',
    variant = 'contained',
    color = 'primary',
    children,
    sx,
    disabled = false,
    autoFocus = false,
}) => {
    sx = { textTransform: 'none', ...sx };
    return (
        <Button
            variant={variant}
            color={color}
            type={type}
            className={className}
            onMouseDown={onMouseDown}
            onClick={onClick}
            sx={sx}
            disabled={disabled}
            autoFocus={autoFocus}
        >
            {children}
        </Button>
    );
};

interface SimpleCheckboxProps {
    name: string;
    onChange: (event: ChangeEvent<HTMLInputElement>) => void;
    label?: string;
    checked: boolean;
    color?: Colors;
    sx?: SxProps<Theme>;
    disabled?: boolean;
}

export const SimpleCheckbox: React.FC<SimpleCheckboxProps> = ({
    name,
    onChange = () => {},
    label,
    checked,
    color = 'primary',
    sx = {},
    disabled,
}) => {
    return (
        <FormControlLabel
            control={
                <Checkbox
                    name={name}
                    checked={!!checked}
                    onChange={onChange}
                    color={color}
                    disabled={disabled}
                />
            }
            sx={sx}
            label={label}
        />
    );
};

interface SimpleRadioProps {
    name: string;
    onChange: (event: ChangeEvent<HTMLInputElement>) => void;
    label?: string;
    value: string;
    checked: boolean;
    color?: Colors;
    sx?: SxProps<Theme>;
    disabled?: boolean;
}

export const SimpleRadio: React.FC<SimpleRadioProps> = ({
    name,
    onChange = () => {},
    label,
    checked,
    value,
    color = 'primary',
    sx = {},
    disabled,
}) => {
    return (
        <FormControlLabel
            control={
                <Radio
                    name={name}
                    value={value}
                    checked={!!checked}
                    onChange={onChange}
                    color={color}
                    sx={sx}
                    disabled={disabled}
                />
            }
            label={label}
        />
    );
};

interface SimpleTextFieldProps {
    name: string;
    label?: string;
    onChange: (event: ChangeEvent<HTMLInputElement>) => void;
    value: string;
    variant?: 'outlined' | 'filled' | 'standard';
    type?: 'input' | 'textarea';
    textFieldType?: string;
    sx?: SxProps<Theme>;
    error?: any;
    disabled?: boolean;
}

export const SimpleTextField: React.FC<SimpleTextFieldProps> = ({
    name,
    label,
    onChange = () => {},
    value,
    variant = 'standard',
    type = 'input',
    textFieldType,
    sx = { width: '100%', mb: 2 },
    error,
    disabled,
}) => {
    return (
        <TextField
            name={name}
            label={label}
            onChange={onChange}
            value={value}
            variant={variant}
            multiline={type === 'textarea'}
            fullWidth
            size="small"
            error={!!error}
            helperText={error?.message}
            sx={sx}
            disabled={disabled}
            type={textFieldType}
        />
    );
};

interface SimpleMultipleSelectProps {
    name: string;
    label: string;
    value: number[]; // multiple selected values
    onChange: (values: number[]) => void;
    options: Option[];
    sx?: SxProps<Theme>;
    error?: any;
    disabled?: boolean;
}

export const SimpleMultipleSelect: React.FC<SimpleMultipleSelectProps> = ({
    name,
    label,
    value,
    onChange,
    options,
    sx = { width: '100%', mb: 2 },
    error,
    disabled,
}) => {
    const handleChange = (event: SelectChangeEvent<number[]>) => {
        const selectedValues = event.target.value as number[];
        onChange(selectedValues);
    };

    return (
        <FormControl size="small" sx={sx} disabled={disabled}>
            <InputLabel id={`${name}-label`}>{label}</InputLabel>
            <Select
                labelId={`${name}-label`}
                multiple
                value={value}
                onChange={handleChange}
                label={label}
                renderValue={(selected) => {
                    if (!selected || selected.length === 0) return <em>{label}</em>;
                    return options
                        .filter((opt) => selected.map(Number).includes(Number(opt.id)))
                        .map((opt) => opt.name)
                        .join(', ');
                }}
            >
                {options.map((option) => (
                    <MenuItem key={option.id} value={option.id}>
                        <Checkbox checked={value.includes(Number(option.id))} />
                        <ListItemText primary={option.name} />
                    </MenuItem>
                ))}
            </Select>
            <ErrorMessage error={error} />
        </FormControl>
    );
};

interface SimpleSwitchProps {
    name: string;
    label?: React.ReactNode;
    labelPlacement?: 'bottom' | 'end' | 'start' | 'top';
    color?: Colors;
    onChange: (event: ChangeEvent<HTMLInputElement>) => void;
    checked?: boolean;
    sx?: SxProps<Theme>;
    disabled?: boolean;
}

export const SimpleSwitch: React.FC<SimpleSwitchProps> = ({
    name,
    label,
    labelPlacement = 'end',
    color = 'primary',
    onChange,
    checked,
    sx = {},
    disabled,
}) => {
    return (
        <FormControlLabel
            control={
                <Switch
                    name={name}
                    checked={checked}
                    color={color}
                    onChange={onChange}
                    disabled={disabled}
                    sx={sx}
                />
            }
            label={label}
            labelPlacement={labelPlacement}
        />
    );
};
